import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { alpha, useTheme } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import {
  createCourierControl,
  deleteCourierControl,
  getCourierControl,
  listCourierControl,
  updateCourierControl,
} from "../../api/CourierControl/courierControlApi";
import { getUserById } from "../../api/User/userApi";
import {
  canCourierAccess,
  canCourierSolicitar,
  canCourierTerminar,
} from "../../utils/courierPermissions";
import {
  pageContentCardSx,
  pageFieldSx,
  pageHeroShellSx,
  pageKpiChipSx,
  pagePrimaryButtonSx,
  pageSectionTitleSx,
} from "../../styles/pageModernStyles";
import CourierControlDialog, {
  emptyEnvio,
  emptyLine,
} from "./CourierControlDialog";

const fmtNum = (value, digits = 2) => {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

const fmtDate = (value) => {
  const raw = String(value || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "—";
  const [y, m, d] = raw.split("-");
  return `${d}/${m}/${y}`;
};

const statusChipSx = (theme, estado) => {
  const map = {
    PEDIDO: theme.palette.warning.main,
    EN_CURSO: theme.palette.info.main,
    ENTREGADO: theme.palette.success.main,
    CANCELADO: theme.palette.text.secondary,
  };
  const color = map[estado] || theme.palette.primary.main;
  return {
    ...pageKpiChipSx,
    height: 26,
    fontWeight: 800,
    bgcolor: alpha(color, 0.14),
    color,
    border: `1px solid ${alpha(color, 0.45)}`,
  };
};

const directionChipSx = (theme, direccion) => {
  const color =
    direccion === "ENTRADA"
      ? theme.palette.secondary?.main || "#7b1fa2"
      : theme.palette.primary.main;
  return {
    ...pageKpiChipSx,
    height: 26,
    fontWeight: 700,
    bgcolor: alpha(color, 0.12),
    color,
    border: `1px solid ${alpha(color, 0.4)}`,
  };
};

const filterToggleSx = (theme) => ({
  borderRadius: "10px !important",
  border: `1px solid ${alpha(theme.palette.divider, 0.85)} !important`,
  overflow: "hidden",
  bgcolor: alpha(theme.palette.background.paper, 0.85),
  "& .MuiToggleButton-root": {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.78rem",
    px: 1.5,
    border: "none !important",
    color: theme.palette.text.secondary,
    "&.Mui-selected": {
      bgcolor: alpha(theme.palette.primary.main, 0.14),
      color: theme.palette.primary.main,
      "&:hover": {
        bgcolor: alpha(theme.palette.primary.main, 0.2),
      },
    },
  },
});

const tableHeadCellSx = (theme) => ({
  fontWeight: 800,
  fontSize: "0.68rem",
  letterSpacing: 0.35,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  bgcolor: alpha(theme.palette.background.default, 0.55),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
  whiteSpace: "nowrap",
  py: 1.25,
});

export default function CourierControl() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [direccion, setDireccion] = useState("");
  const [ambito, setAmbito] = useState("");
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(emptyEnvio());
  const [saving, setSaving] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permsLoaded, setPermsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const decoded = token ? jwtDecode(token) : null;
        const userId = decoded?.userId || decoded?.id;
        if (!userId) {
          if (!cancelled) {
            setPermUser({ isAdmin: !!decoded?.isAdmin });
            setPermsLoaded(true);
          }
          return;
        }
        const user = await getUserById(userId);
        if (!cancelled) {
          setPermUser({
            ...(user || {}),
            isAdmin: user?.isAdmin ?? decoded?.isAdmin,
          });
          setPermsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setPermUser({});
          setPermsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const allowAccess = canCourierAccess(permUser || {});
  const allowSolicitar = canCourierSolicitar(permUser || {});
  const allowTerminar = canCourierTerminar(permUser || {});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listCourierControl({
        direccion: direccion || undefined,
        ambito: ambito || undefined,
        q: q.trim() || undefined,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || t("courierControl.loadError"));
    } finally {
      setLoading(false);
    }
  }, [direccion, ambito, q, t]);

  useEffect(() => {
    if (!permsLoaded || !allowAccess) return undefined;
    const timer = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, q, permsLoaded, allowAccess]);

  const openDraft = (envio) => {
    setDraft({
      ...emptyEnvio(),
      ...envio,
      Fecha: String(envio.Fecha || "").slice(0, 10),
      lineas: envio.lineas?.length
        ? envio.lineas.map((linea) => emptyLine(linea))
        : [emptyLine()],
    });
    setDialogOpen(true);
  };

  useEffect(() => {
    if (!permsLoaded || !allowAccess) return;
    const nueva = params.get("nueva");
    const id = params.get("id");
    const muestraId = params.get("muestraId");
    const codigo = params.get("codigo") || "";

    if (nueva === "1") {
      if (!allowSolicitar) {
        setParams({}, { replace: true });
        return;
      }
      openDraft(
        emptyEnvio({
          lineas: [
            emptyLine({
              IdMuestra: muestraId ? Number(muestraId) : null,
              CodigoMuestra: codigo,
            }),
          ],
        }),
      );
      setParams({}, { replace: true });
      return;
    }

    if (id) {
      getCourierControl(id)
        .then((envio) => openDraft(envio))
        .catch(() => setError(t("courierControl.loadError")));
      setParams({}, { replace: true });
    }
  }, [params, setParams, t, permsLoaded, allowAccess, allowSolicitar]);

  const save = async () => {
    if (draft.IdCourierControl) {
      if (!allowTerminar) return;
    } else if (!allowSolicitar) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (draft.IdCourierControl) {
        await updateCourierControl(draft.IdCourierControl, draft);
      } else {
        await createCourierControl(draft);
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || t("courierControl.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!draft.IdCourierControl || !allowTerminar) return;
    if (!window.confirm(t("courierControl.deleteConfirm"))) return;
    setSaving(true);
    try {
      await deleteCourierControl(draft.IdCourierControl);
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || t("courierControl.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const directionLabel = (value) =>
    value === "ENTRADA" ? t("courierControl.entry") : t("courierControl.exit");
  const scopeLabel = (value) =>
    value === "INTERNACIONAL"
      ? t("courierControl.international")
      : t("courierControl.national");
  const statusLabel = (value) => {
    if (value === "EN_CURSO") return t("courierControl.statusEnCurso");
    if (value === "ENTREGADO") return t("courierControl.statusEntregado");
    if (value === "CANCELADO") return t("courierControl.statusCancelado");
    return t("courierControl.statusPedido");
  };

  const kpis = useMemo(() => {
    const counts = { total: rows.length, PEDIDO: 0, EN_CURSO: 0, ENTREGADO: 0 };
    rows.forEach((row) => {
      if (counts[row.Estado] != null) counts[row.Estado] += 1;
    });
    return counts;
  }, [rows]);

  const openRow = (id) => {
    getCourierControl(id)
      .then(openDraft)
      .catch(() => setError(t("courierControl.loadError")));
  };

  if (permsLoaded && !allowAccess) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {t("courierControl.noPermission")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.5 },
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        minHeight: "100%",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 28%)`
            : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, transparent 32%)`,
      }}
    >
      <Paper sx={pageHeroShellSx(theme)} elevation={0}>
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "flex-start" },
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", minWidth: 0, flex: 1, gap: 1.5 }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2.5,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.secondary?.main || "#7b1fa2", 0.18)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
                }}
              >
                <LocalShippingOutlinedIcon
                  sx={{ color: "primary.main", fontSize: 28 }}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: 1.1, display: "block", fontWeight: 800 }}
                >
                  {t("courierControl.overline")}
                </Typography>
                <Typography
                  component="h1"
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.15,
                    fontSize: { xs: "1.35rem", md: "1.65rem" },
                  }}
                >
                  {t("courierControl.title")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.6, maxWidth: 720, lineHeight: 1.45 }}
                >
                  {t("courierControl.subtitle")}
                </Typography>
              </Box>
            </Box>

            {allowSolicitar ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openDraft(emptyEnvio())}
                sx={{
                  ...pagePrimaryButtonSx(theme),
                  alignSelf: { xs: "stretch", md: "center" },
                  whiteSpace: "nowrap",
                }}
              >
                {t("courierControl.newRequest")}
              </Button>
            ) : null}
          </Box>

          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
            <Chip
              size="small"
              label={t("courierControl.kpiTotal", { count: kpis.total })}
              sx={{
                ...pageKpiChipSx,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                fontWeight: 800,
              }}
            />
            <Chip
              size="small"
              label={`${kpis.PEDIDO} ${t("courierControl.statusPedido")}`}
              sx={statusChipSx(theme, "PEDIDO")}
            />
            <Chip
              size="small"
              label={`${kpis.EN_CURSO} ${t("courierControl.statusEnCurso")}`}
              sx={statusChipSx(theme, "EN_CURSO")}
            />
            <Chip
              size="small"
              label={`${kpis.ENTREGADO} ${t("courierControl.statusEntregado")}`}
              sx={statusChipSx(theme, "ENTREGADO")}
            />
          </Stack>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          ...pageContentCardSx(theme),
          mb: 2,
          py: 1.5,
          px: { xs: 1.5, md: 2 },
        }}
      >
        <Typography sx={{ ...pageSectionTitleSx, mb: 1.25 }}>
          {t("courierControl.filters")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={direccion}
            onChange={(_, value) => setDireccion(value || "")}
            sx={filterToggleSx(theme)}
          >
            <ToggleButton value="">{t("courierControl.all")}</ToggleButton>
            <ToggleButton value="SALIDA">
              {t("courierControl.exit")}
            </ToggleButton>
            <ToggleButton value="ENTRADA">
              {t("courierControl.entry")}
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={ambito}
            onChange={(_, value) => setAmbito(value || "")}
            sx={filterToggleSx(theme)}
          >
            <ToggleButton value="">{t("courierControl.all")}</ToggleButton>
            <ToggleButton value="NACIONAL">
              {t("courierControl.national")}
            </ToggleButton>
            <ToggleButton value="INTERNACIONAL">
              {t("courierControl.international")}
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            placeholder={t("courierControl.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{
              ...pageFieldSx(theme),
              minWidth: { xs: "100%", sm: 280 },
              flex: { sm: "1 1 240px" },
              maxWidth: 420,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{ ...pageContentCardSx(theme), p: 0, overflow: "hidden" }}
      >
        <TableContainer sx={{ maxWidth: "100%" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.date")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.direction")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.origin")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.destination")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.createdBy")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.content")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.pieces")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.volume")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.volWeight")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.grossWeight")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.chargeable")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.cost")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.realCost")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.difference")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.awb")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.status")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={16} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={16} align="center" sx={{ py: 6 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        color: "text.secondary",
                      }}
                    >
                      <LocalShippingOutlinedIcon
                        sx={{ fontSize: 36, opacity: 0.45 }}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {t("courierControl.empty")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const diff = Number(row.Diferencia);
                  const diffColor =
                    Number.isFinite(diff) && diff !== 0
                      ? diff > 0
                        ? theme.palette.error.main
                        : theme.palette.success.main
                      : "inherit";
                  return (
                    <TableRow
                      key={row.IdCourierControl}
                      hover
                      sx={{
                        cursor: "pointer",
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                        },
                        "& td": {
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
                          py: 1.15,
                        },
                      }}
                      onClick={() => openRow(row.IdCourierControl)}
                    >
                      <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                        {fmtDate(row.Fecha)}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap={0.75} flexWrap="wrap">
                          <Chip
                            size="small"
                            label={directionLabel(row.Direccion)}
                            sx={directionChipSx(theme, row.Direccion)}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={scopeLabel(row.Ambito)}
                            sx={{
                              ...pageKpiChipSx,
                              height: 26,
                              fontWeight: 600,
                            }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 160 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          title={row.Origen || ""}
                        >
                          {row.Origen || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 180 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          title={row.Destino || ""}
                        >
                          {row.Destino || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 120 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          title={row.CreatedByName || row.SolicitadoPor || ""}
                        >
                          {row.CreatedByName || row.SolicitadoPor || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, minWidth: 140 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          title={
                            Array.isArray(row.contenidoLabels) &&
                            row.contenidoLabels.length
                              ? row.contenidoLabels.join(", ")
                              : row.contenido || ""
                          }
                          sx={{ fontWeight: 600 }}
                        >
                          {row.contenido || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{row.piezas || 0}</TableCell>
                      <TableCell align="right">
                        {fmtNum(row.VolumenM3, 4)}
                      </TableCell>
                      <TableCell align="right">
                        {fmtNum(row.PesoVolumetricoKg, 3)}
                      </TableCell>
                      <TableCell align="right">
                        {fmtNum(row.PesoBrutoKg, 3)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {fmtNum(row.PesoFacturableKg, 3)}
                      </TableCell>
                      <TableCell align="right">
                        {fmtNum(row.Coste, 2)}
                      </TableCell>
                      <TableCell align="right">
                        {fmtNum(row.CosteReal, 2)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, color: diffColor }}
                      >
                        {fmtNum(row.Diferencia, 2)}
                      </TableCell>
                      <TableCell>
                        {row.NumeroAwb ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "ui-monospace, monospace",
                              fontWeight: 700,
                              fontSize: "0.8rem",
                            }}
                          >
                            {row.NumeroAwb}
                          </Typography>
                        ) : row.Estado === "PEDIDO" ? (
                          <Chip
                            size="small"
                            label={t("courierControl.pendingBox")}
                            sx={statusChipSx(theme, "PEDIDO")}
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusLabel(row.Estado)}
                          sx={statusChipSx(theme, row.Estado)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CourierControlDialog
        open={dialogOpen}
        envio={draft}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onChange={setDraft}
        onSave={save}
        onDelete={remove}
        canSolicitar={allowSolicitar}
        canTerminar={allowTerminar}
      />
    </Box>
  );
}
